import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import BuildColumns from './helpers/Columns';
import CreateEditStaffRoleCard from './components/CreateEditStaffRoleCard';
import MobileRowMainContent from './components/MobileRowStructure';
import { StepContext } from '../../context/StepContext';
import { UserContext } from '../../context/UserContext';
import useBusinessInformation from '../../helperHooks/useBusinessInformation';
import useFiltersFetchedData from '../../helperHooks/useFiltersFetchedData';
import { PageMetadata } from '../../components/datagridComponents/paginationHelpers';
import { STAFF_ROLES_CACHE_KEY } from '../../constants';
import { StaffRole, StaffRoleErrors } from '../../types';
import PageHeader from '../../components/pageHeaderComponents/PageHeader';
import ActionsContainer from '../../components/ActionsContainer';
import { FilterContextProvider } from '../../context/FilterContext';
import GenericDataGridAndSideForm from '../../components/datagridComponents/GenericDataGridAndSideForm';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import usePageTitle from '../../helperHooks/usePageTitle';

const StaffRolesContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goToPreviousStep } = useContext(StepContext);
  const { locale } = useContext(UserContext);
  const {
    currencySymbol,
    currencyISOCode,
    isLoading: isLoadingCurrency,
  } = useBusinessInformation();

  usePageTitle('nav.roles');

  const { fetchData, data, isLoading, isError, error, isRefetching } = useFiltersFetchedData<{
    data: StaffRole[];
    metadata: PageMetadata;
  }>(
    STAFF_ROLES_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/staff-roles`,
    'staff roles'
  );

  const buildColumns = useCallback(
    (): GridColDef[] =>
      BuildColumns({ currencyISOCode, currencySymbol, isLoading: isLoadingCurrency }, locale),
    [currencyISOCode, currencySymbol, isLoadingCurrency, locale]
  );

  const unauthorized = (error as any)?.code === 401;

  return (
    <>
      <PageHeader omitTextHeader />
      <GenericDataGridAndSideForm<StaffRole, StaffRoleErrors>
        data={data?.data}
        metadata={data?.metadata}
        isLoading={isLoading || isRefetching}
        isError={isError}
        unauthorized={unauthorized}
        fetchData={fetchData}
        BuildColumns={buildColumns}
        CreateComponent={CreateEditStaffRoleCard}
        translationKey="staffRoles"
        endpoint={`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/staff-roles`}
        sentryMsg="staff roles"
        cacheKey={STAFF_ROLES_CACHE_KEY}
        pageDescription={t('screens.staffRoles.staffRolesScreenTooltip')}
        mobileRowMainContent={MobileRowMainContent}
      />
      <ActionsContainer>
        <Button
          variant="outlined"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={goToPreviousStep}
        >
          {t('actions.previous')}
        </Button>
        <Button
          variant="contained"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={() => {
            navigate('/staff-list');
          }}
        >
          {t('actions.next')}
        </Button>
      </ActionsContainer>
    </>
  );
};

const StaffRoles = () => (
  <FilterContextProvider>
    <StaffRolesContent />
  </FilterContextProvider>
);

export default StaffRoles;
